import { Box, Dialog, DialogActions, DialogContent, FormHelperText, Grid, RadioGroup, Stack } from "@mui/material";
import { MprcContainer, MprcDialogTitleClose, MprcRequestErrorReport } from "components";
import { ButtonCancel, ButtonSave } from "components/buttons";
import { AutoCompleteHierarchicalModel } from "components/MprcAutoCompleteHierarchical";
import { MprcRadio } from "components/MprcRadio";
import useDidUpdateEffect from "hooks/useNoInitialEffect";
import { useCoreApi } from "providers/CoreApiProvider";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { QuestionGroupAutoCompleteHierarchical } from "views/assessment/questionBank/components/QuestionGroupAutoCompleteHierarchical";
import { QuestionsSelectListBox } from "views/assessment/questionSelect/QuestionsSelectListBox";

/////////////////////////////////////////////////////////////////////////////////

export enum ModeSelectType {
    Question = 1,
    AllQuestion = 2,
}

interface IPros {
    onClose: (questionGroup: AutoCompleteHierarchicalModel[] | null, questions: string[] | null, groupQuestionTotal: number | null) => void;
}

/////////////////////////////////////////////////////////////////////////////////

export const EditTopicChallengeSelectQuestionsDialog: React.FC<IPros> = (props) => {

    const { onClose } = props;
    const { t } = useTranslation("assessment");
    const api = useCoreApi();

    const [viewError, setViewError] = useState<any>();
    const [questionIds, setQuestionIds] = useState<string[]>([]);
    const [questionGroup, setQuestionGroup] = useState<AutoCompleteHierarchicalModel[]>([]);
    const [selectType, setSelectType] = useState<ModeSelectType>(ModeSelectType.AllQuestion);
    const [totalQuestions, setTotalQuestions] = useState<number>(0);

    const canSave = questionGroup.length > 0 && (selectType == ModeSelectType.AllQuestion || questionIds.length > 0);

    ///
    function updateTotals() {
        if (questionGroup.length > 0)
            api.v1.questionBank.questionGroupKnowledgeTestInfo({ questionGroupId: questionGroup[0].key })
                .then(result => {
                    setTotalQuestions(result.data.totalQuestions);
                }).catch(e => setViewError(e));
        else
            setTotalQuestions(0);
    }

    ///
    const handleSave = () => {
        if (selectType == ModeSelectType.AllQuestion)
            onClose(questionGroup, null, totalQuestions);
        else
            onClose(null, questionIds, null);
    };

    ////////////////////////////////////////////////////////////////////////////
    /// EFFECT'S
    ////////////////////////////////////////////////////////////////////////////

    // Ao editar informações
    useDidUpdateEffect(() => {
        updateTotals();
    }, [questionGroup]);

    ////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////

    return (
        <Dialog
            open
            fullWidth
            maxWidth="md"
            onClose={() => onClose(null, null, null)}
        >
            <MprcDialogTitleClose
                title={t("TopicChallenge.Edit.QuestionsSelect.TitleView")}
                onClose={() => onClose(null, null, null)}
                shrinkPadding
            />
            <DialogContent style={{ minHeight: "60vh" }}>
                <MprcRequestErrorReport error={viewError} />

                <MprcContainer>
                    <Box padding={1} sx={
                        (theme) => ({
                            backgroundColor: theme.palette.notice.main,
                            borderRadius: "6px"
                        })
                    }>
                        <QuestionGroupAutoCompleteHierarchical
                            label={t("TopicChallenge.Edit.QuestionsSelect.QuestionGroupLabel")}
                            placeholder={t("TopicChallenge.Edit.QuestionsSelect.QuestionGroupPlaceholder")}
                            multiSelect={false}
                            autoFocus
                            forSurvey={false}
                            selectedItems={questionGroup}
                            onChange={(items) => setQuestionGroup(items)}
                            onError={(error) => setViewError(error)}
                        />
                        <FormHelperText>{t("TopicChallenge.Edit.QuestionsSelect.TotalQuestionGroupText", { count: totalQuestions })}</FormHelperText>
                    </Box>
                </MprcContainer>

                <Box padding={1}>
                    <RadioGroup
                        value={selectType}
                        onChange={(e) => setSelectType(e.target.value as any as ModeSelectType)}
                    >
                        <Grid container direction="column" spacing={0}>
                            <Grid>
                                <MprcRadio
                                    value={ModeSelectType.AllQuestion}
                                    label={t("TopicChallenge.Edit.QuestionsSelect.SelectOptionAllLabel")}
                                />
                            </Grid>
                            <Grid>
                                <MprcRadio
                                    value={ModeSelectType.Question}
                                    label={t("TopicChallenge.Edit.QuestionsSelect.SelectOptionQuestionLabel")}
                                />
                            </Grid>
                        </Grid>
                    </RadioGroup>
                    <Box height={"350px"}>
                        <QuestionsSelectListBox
                            questionGroupId={questionGroup.length == 0 ? null : questionGroup[0].key}
                            selected={questionIds}
                            disabled={selectType == ModeSelectType.AllQuestion}
                            forSurvey={false}
                            onlyKnowledgeTests
                            onSelected={(selected) => setQuestionIds(selected)}
                            onError={(e) => setViewError(e)}
                        />
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions>
                <Stack direction="row" spacing={1} justifyContent="right" width="100%" marginX={1}>
                    <ButtonCancel
                        variant="contained"
                        onClick={() => onClose(null, null, null)}
                    />
                    <ButtonSave
                        variant="contained"
                        disabled={!canSave}
                        onClick={handleSave}
                    />
                </Stack>
            </DialogActions>
        </Dialog>
    );
};
